import { Component, ViewChild, Input, Output, EventEmitter, OnDestroy, ElementRef, SimpleChanges } from '@angular/core';
import { LfToastMessage, LfMessageToastTypes } from '@lf-web-components/lf-message-toast';
import { LocalizePipe } from '@lf-web-components/lf-common-utils-library';
import { OrganizerTableComponent } from '../organizer-table/organizer-table.component';
import { RepositoryService } from '../services/resource/repository.service';
import { ErrorType, FileSetError, TargetType, ValidatorService } from '../services/validation/validator.service';
import { UpdateFilePropertyService } from '../services/update-file-property.service';
import { EntryMetaData, EntryMetaDataModel, FileSetFieldModel } from '../shared/fs-entry.model';

import { ModeType, MIN_FIELD_WIDTH, UPLOAD_LIMIT, ColumnModel, DocumentOrganizerType, FileSetEntryViewModel, InitErrors, InstanceModel, StatePropertyValueModel, ImportMode, ImportOrigin, EmptyPropertyKey, FileStateModel, DEFAULT_COLUMN_TO_USE, AVAILABLE_COLUMNS, StateContextKey } from '../shared/fs.model';

import { FilesetService } from '../services/resource/fileset.service';
import { BaseDialog, DialogService } from '@lf-web-components/lf-dialog-library';
import { Subscription, ReplaySubject } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { DOCUMENT } from '@angular/common';

import ResizeObserver from 'resize-observer-polyfill';
import { NgZone, Inject } from '@angular/core';
import { isGuid, lfFilesetLocalize, generateToast, isFileSetValid, coerceStrToBoolean, decodeHtmlEntities, prepareFallbackId } from '../shared/utils';
import { FieldParsingService } from '../services/resource/field-parsing.service';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';



@Component({
  selector: 'lf-fileset-shell',
  templateUrl: './fileset-shell.component.html',
  styleUrls: ['./fileset-shell.component.css'],
  providers: [ ValidatorService, UpdateFilePropertyService ]
})
export class FilesetShellComponent implements OnDestroy {

  /**
   * The Fileset resource (guid)
   */
  @Input() docId: string;

  /**
   * The Fileset instance id (guid)
   */
  @Input() insId: string;

  @Input() allowDownload: string | boolean;
  
  @Input() showErrors: string | boolean;

  @Input() highlightErrors: string | boolean;

  /**
   * The ID of the required status
   * Required status must be set for all files
   */
  @Input() requiredStatus: string;

  /**
   * The name of the required status
   */
  @Input() requiredStatusName: string;

  /**
   * Import Mode
   * 0 - None, import is disabled
   * 1 - Local, only import from local is supported
   * 2 - Repository, only import from repository is supported
   * 3 - Both, import from local and repository are supported
   */
  @Input() importMode: number = ImportMode.None;

  /**
   * Provide state json instead of retrieving it via insId
   */
  @Input() inState: string;

  /**
   *  Customizable columns to display
   * Format is array of column key strings
   */
  @Input() columns: string | string[];

  /**
   * Display modes
   *  edit or default is edit mode where all actions are supported
   *  print - data is displayed in rows in readonly format, toolbar hidden
   *  readonly - only data is displayed, toolbar hidden
   *  disabled - everything is shown, but cannot be interacted with
   */
  @Input() mode: ModeType = ModeType.EDIT;

  @Input() maxHeight: number = 500;

  @Input() initError: InitErrors = InitErrors.NONE;

  @Input() parentFieldId: string;

  /**
   * Create new instance of file set and merge 
   * instance data from different file set
   */
  @Input() mergeNew: string | boolean;

  @Output() errorChanged = new EventEmitter<FileSetError[]>();

  @Output() modelChange = new EventEmitter<InstanceModel>();

  private errorChangedSub: Subscription;

  @ViewChild(OrganizerTableComponent)
  private tableRef: OrganizerTableComponent

  width: number = MIN_FIELD_WIDTH;
  instanceModel: InstanceModel;
  fileEntries: FileSetEntryViewModel[] = [];
  typeViewModel: DocumentOrganizerType[] = [];
  displayColumns: ColumnModel[] = [];
  metaData: EntryMetaDataModel = {};
  toastMessages: LfToastMessage[] = [];
  fields: FileSetFieldModel[] = []

  statusViewModel = [];
  additionalColumns: string[];
  documentCount: number;
  fileSetId: string;
  repoId: string;
  defaultSourceFolder: string;
  destinationFolder: string;

  waBaseURL: string;
  repoName: string;

  readOnly: boolean;
  disabled: boolean;
  print: boolean;
  availableColumns = AVAILABLE_COLUMNS;
  createTemplate = false;
  initialized = false;
  initializing: boolean;

  requiredStatusInfo: StatePropertyValueModel;

  uploadLimit = UPLOAD_LIMIT;
  targetType = EmptyPropertyKey.Type;
  importDialogRef: MatDialogRef<BaseDialog>;

  totalFilesAddedFromRepo: number = 0; 
  totalFilesAddedFromLocal: number = 0;

  private _document?: Document;

  // Ensures file set instance is initialized before updating newSourceFolderId and newDestinationFolderId
  private initInstanceSubject: ReplaySubject<any> = new ReplaySubject(1);

  // Dictionary of property keys successfully updated by external values
  // do not override these values on refresh
  private newPropertyValueUpdated: {} = {};

  constructor(
    private ele:ElementRef,
    private localizePipe: LocalizePipe,
    private resource: FilesetService,
    private repositoryService: RepositoryService,
    public validatorService: ValidatorService,
    private updateFilePropertyService: UpdateFilePropertyService,
    private dialogService: DialogService, 
    private fieldParsingService: FieldParsingService,
    private zone:NgZone, 
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    @Inject(DOCUMENT) readonly document?: any
    ) {
    this.documentCount = this.fileEntries.length;
    this._document = document as Document;
    this.iconRegistry.addSvgIcon(
      'error_outline', // Icon name
      this.sanitizer.bypassSecurityTrustResourceUrl(window['LF']?.Url?.contentRoot + 'img/error_outline.svg')
    );
    this.iconRegistry.addSvgIcon(
      'clear', // Icon name
      this.sanitizer.bypassSecurityTrustResourceUrl(window['LF']?.Url?.contentRoot + 'img/clear.svg')
    );
  }

  private newInstanceCreated = false;

  @Input()
  get state() {
    this.saveState();
    return this.instanceModel;
  }

  @Input()
  set save(doSave: boolean) {
    if (doSave) {
      this.saveInstance(); 
    }
  }

  @Input()
  get errors() {
    if (this.initError == InitErrors.NONE) { // only validate files with valid instances
      this.validatorService.validateInstanceData(this.fileEntries);
      return this.validatorService.errors;
    }
    return [];
  }

  @Input()
  get isValid() {
    return isFileSetValid(this.initError);
  }

  @Input()
  set showErrorMessages(value: boolean) {
    this.showErrors = value;
  }

  /**
   * Update to a new fileset id
   *  If no instance created yet, load new fileset id
   *  If instance created, copy over new fileset properties to this instance
   */
  @Input()
  set newFileSetId(id: string) {
    if (isGuid(id)) {
      if (id != this.docId) {
        this.initializing = true;
        this.validatorService.errors = [];
        this.toastMessages = [];
        this.resource.getTemplate(id)
        .subscribe(
          data => {
            if (this.repoId && this.repoId !== data.Context.RepositoryId && this.fileEntries.length > 0) {
              this.toastMessages = [ generateToast(LfMessageToastTypes.Error, this.localizePipe.transform("lf_fileset_invalid_repository_error", lfFilesetLocalize)) ]
              this.initializing = false;
            } else {
              this.newPropertyValueUpdated[StateContextKey.FileSetId] = true;
              this.initInstanceSubject.next(data);

              // reset previous instance values
              this.docId = id;
              this.initError = InitErrors.NONE;
              this.readOnly = this.mode.toUpperCase() == ModeType.READONLY;
              this.disabled = this.mode.toUpperCase() == ModeType.DISABLED;
              this.createTemplate = true;
              this.validatorService.errors = []; // reset again in case of timing issue
              this.toastMessages = [];
              
              if (this.instanceModel) { // update if there is current file set instance
                this.instanceModel.State.FileStates = this.generateStateModel(this.fileEntries); // copy over current files
                this.updateFilePropertyService.updateFileProperties(this.fileEntries, this.instanceModel, data);

                // copy over relevant data to new instance
                data.Context.MetaDataCache = this.instanceModel.Context.MetaDataCache;
                data.State.FileStates = this.instanceModel.State.FileStates;

                if (this.inState) {
                  this.inState = JSON.stringify(data); // update inState
                }
              }
              this.initializing = false;
              this.initialize(data, false);
            }
          },
          error => {
            this.docId = id;
            this.newPropertyValueUpdated[StateContextKey.FileSetId] = true;

            this.handleResourceError(error);
          });
      }
    } else {
      // reset previous instance values
      this.validatorService.errors = [];
      this.toastMessages = [];
      this.docId = undefined;

      if (id) {
        this.disabled = true;
        this.initError = InitErrors.RESOURCE_NOT_FOUND;
      } else {
        this.readOnly = true;
        this.initError = InitErrors.NONE_INITIALIZED;
        this.errorInit();
      }
    }
  }

  /**
   * Update to a new default source folder id
   */
  @Input()
  set newSourceFolderId(id: string) {
    if (id) {
      this.initInstanceSubject.subscribe(
        data => {
          this.repoId = data.Context.RepositoryId;
          this.defaultSourceFolder = id;
          this.repositoryService.getRepositoryEntryProp(this.repoId, this.defaultSourceFolder)
          .subscribe(
            data => {
              this.defaultSourceFolder = id;
              this.newPropertyValueUpdated[StateContextKey.SourceFolderId] = true;
              this.modelChange.emit(this.state);
            }, 
            error => {
              this.defaultSourceFolder = id;
              this.newPropertyValueUpdated[StateContextKey.SourceFolderId] = true;
              this.showErrorToast(this.localizePipe.transform({ key: 'lf_fileset_fail_update_source_folder', args: [id] }, lfFilesetLocalize));
            });
          });
    }
  }

  /**
   * Update to a new destination folder id
   */
  @Input()
  set newDestinationFolderId(id: string) {
    if (id) {
      this.initInstanceSubject.subscribe(
        data => {
          this.repoId = data.Context.RepositoryId;
          this.destinationFolder = id;
          this.repositoryService.getRepositoryEntryProp(this.repoId, this.destinationFolder)
          .subscribe(
            data => {
              this.destinationFolder = id;
              this.newPropertyValueUpdated[StateContextKey.DestinationFolderId] = true;
              this.modelChange.emit(this.state);
            }, 
            error => {
              this.destinationFolder = id;
              this.newPropertyValueUpdated[StateContextKey.DestinationFolderId] = true;
              this.showErrorToast(this.localizePipe.transform({ key: 'lf_fileset_fail_update_dest_folder', args: [id] }, lfFilesetLocalize));
            });
          });
    }
  }

  private showInfoToast(msg: string, clear = true) {
    this.showToast(LfMessageToastTypes.Informational, msg, clear);
  }

  private showErrorToast(msg: string, clear = true) {
    this.showToast(LfMessageToastTypes.Error, msg, clear);
  }

  private showToast(type: LfMessageToastTypes, msg, clear) {
    if (clear) {
      this.toastMessages = [];
    }
    this.toastMessages = [generateToast(type, msg)];
  }

  ngOnInit() {
    this.initFileset();

    // ensure one instance of 'div.fileset-status-container' exist on page
    if (!this._document.querySelector('body div.fileset-status-container')) {
      let e = this._document.createElement("div");
      e.className = "fileset-status-container";
      e.style.cssText = "position:relative";
      this._document.body.appendChild(e);
    }
  }

  ngOnChanges(changes:SimpleChanges) {

    this.allowDownload = coerceStrToBoolean(this.allowDownload);
    this.showErrors = coerceStrToBoolean(this.showErrors);
    this.highlightErrors = coerceStrToBoolean(this.highlightErrors);
    this.mergeNew = coerceStrToBoolean(this.mergeNew);
    this.readOnly = this.mode.toUpperCase() == ModeType.READONLY || this.initError != InitErrors.NONE;
    this.disabled = this.mode.toUpperCase() == ModeType.DISABLED;
    this.print = this.mode.toUpperCase() == ModeType.PRINT;

    if (changes['columns']) {
      this.initializeAllColumns();
    }
    
  }

  initFileSetEntryModel(fileState){
    this.fileEntries = fileState.map(state => {

      let meta = this.metaData[state.FileId];
      return {
        id: state.FileId, 
        name: meta?.name ?? state.FileId,
        docType: state.FileStateValues.find(sVal => sVal.StateKey == "type")?.Value, 
        status: state.FileStateValues.find(sVal => sVal.StateKey == "status")?.Value,
        properties: meta,
        importOrigin: state.FileStateValues.find(sVal => sVal.StateKey == "importorigin")?.Value
      }
    });

    this.documentCount = this.fileEntries.length;
  }

  validateEntryModel(){
    this.validatorService.setAndUpdateInstanceState(this.instanceModel, this.requiredStatusInfo?.Key, this.displayColumns.find(c => c.key === "status") ? true : false);
    this.validatorService.validateInstanceData(this.fileEntries);
  }

  initFileState(fileState, forceUpdate) {

    function prepareStateModelWithCachedMeta(fileState)
    {
      this.metaData = this.instanceModel.Context.MetaDataCache.reduce((obj, item) => (obj[item.EntryId] = this.fieldParsingService.mapEntryModel(item), obj), {});
      this.initFileSetEntryModel(fileState)
      this.validateEntryModel();
    }

    if (fileState) {
        if (forceUpdate) {
          var fileIds = fileState.map(file => parseInt(file.FileId));

          this.repositoryService.getBulkEntries(this.repoId, fileIds, this.getAdditionalFields()).subscribe(
            {
              next: result => {
                var foundEntries = result.data;
                var foundEntryIds = foundEntries.map(e => e.props.EntryId);
                var notFoundEntryIds = fileIds.filter(id => !foundEntryIds.includes(id));
                
                for (let fileMetaData of result.data){
                    if(!notFoundEntryIds.includes(fileMetaData.props.EntryId)){
                      this.metaData[fileMetaData.props.EntryId] = this.fieldParsingService.mapEntryModel(fileMetaData.props);

                      // update metadata cache
                      this.instanceModel.Context.MetaDataCache = this.instanceModel.Context.MetaDataCache.map(entry => { 
                        if (entry.EntryId == fileMetaData.props.EntryId){
                          entry = fileMetaData.props
                        }
                        return entry;
                      });
                    }
                }

                for (let notFoundEntry of notFoundEntryIds){
                  // retain old metadata to display
                  var oldEntry = this.instanceModel.Context.MetaDataCache.filter(entry => entry.EntryId == notFoundEntry);
                  if (oldEntry.length > 0){
                    this.metaData[notFoundEntry] = this.fieldParsingService.mapEntryModel(oldEntry[0]);
                    this.metaData[notFoundEntry].notFound = true;
                  }
                }
                
                this.initFileSetEntryModel(fileState);
                this.validateEntryModel();

              }, 
              error: e => {
                prepareStateModelWithCachedMeta.call(this, fileState);
              }

            }
          )
        } else {
          prepareStateModelWithCachedMeta.call(this, fileState);
        }
    } else {
      this.validateEntryModel();
    }
  }

  initColumn(columns) {
    this.displayColumns = [];
    for (let column of columns){
      if (this.availableColumns[column]){
        this.displayColumns.push(this.availableColumns[column]);
      } else {
        if (!isNaN(+column)) {
          let fieldColumn = this.fields.filter(field => field.Id.toString() == column);
          if (fieldColumn.length > 0){
            this.displayColumns.push(this.generateFieldColumn(fieldColumn[0]));
          }
        }
      }
    }
  }

  generateFieldColumn(column){
    return {key: column.Id, header: decodeHtmlEntities(column.Name), cell: (element: any) => 
      {
        let columnValue = "";
        let filter = element.properties?.fields?.filter(field => field.Id == column.Id) ?? [];
        if (filter.length > 0){
          columnValue = filter[0].Value;
        }
        return columnValue;
      }
    }
  }

  initFileSetTable(data, forceUpdate: boolean){
    let typeDef = data.State.StateProperties.find(p => p.StateKey === "type");
    let statusDef = data.State.StateProperties.find(p => p.StateKey === "status");
    this.typeViewModel = typeDef.StateProperties.map(t => ({
      'key': t.Key, 
      'display': t.DisplayName, 
      'min': t.Settings["min"], 
      'max': t.Settings["max"],
      'isRequired': function(){ return this.min > 0}
    }));

    this.requiredStatusInfo = statusDef.StateProperties.find(s => s.Key === this.requiredStatus || s.DisplayName === this.requiredStatusName);

    this.statusViewModel = statusDef.StateProperties.map(s=>({
      'key': s.Key, 
      'display': s.DisplayName,
      'isRequired': s.Settings["required"],
      'type': s.Settings["type"]
    }));
    this.initFileState(data.State.FileStates, forceUpdate);
  }

  initializeAllColumns() {

    let self = this;
    let viewColumKeys = DEFAULT_COLUMN_TO_USE;

    if (self.columns) { 
      if (typeof self.columns === 'string') {
        viewColumKeys = JSON.parse(self.columns);
      } else {
        viewColumKeys = self.columns;
      }
      
    } else if (this.additionalColumns)  {
      viewColumKeys = self.additionalColumns;
    } 

    self.initColumn(viewColumKeys);
  }

  initialize(data: InstanceModel, forceUpdate: boolean){

    var self = this;
    self.instanceModel = data;
    self.docId = data.FileSetId; // update in case instance data has different id
    self.insId = data.InstanceId;
    self.fileSetId = data.FileSetId;
    self.waBaseURL = data.Context.WebAccessBaseURL;
    self.repoId = data.Context.RepositoryId;
    self.repoName = data.Context.RepositoryName;
    self.fields = data.Context.FieldCache;
    self.additionalColumns = data.Context.AdditionalColumns;

    function setContextFolderId(setter, key: string) {
      if (!self.newPropertyValueUpdated[key]) {
        setter();
      }
    }

    setContextFolderId(()=>{
      self.defaultSourceFolder = data.Context.SourceFolderId;
    }, StateContextKey.SourceFolderId);

    setContextFolderId(()=>{
      self.destinationFolder = data.Context.DestinationFolderId;
    }, StateContextKey.DestinationFolderId);

    if (data.Context.ContextValueUpdated) {

      [StateContextKey.SourceFolderId, StateContextKey.DestinationFolderId, StateContextKey.FileSetId].forEach((key)=>{
        if (data.Context.ContextValueUpdated[key] === true) {
          this.newPropertyValueUpdated[key] = true;
        }
      });
    }
    
    if (forceUpdate){
      this.toastMessages = [];
      if(this.createTemplate){
        self.instanceModel.State.FileStates = this.generateStateModel(this.fileEntries);
      } else {
        if (this.initialized){
          self.instanceModel.State.FileStates = this.generateStateModel(this.fileEntries);
        }
      }
      this.resource.getFileSetSettings(this.fileSetId).subscribe(
        result => {
          setContextFolderId(()=>{
            self.defaultSourceFolder = result.DefaultSourceFolderId;
          }, StateContextKey.SourceFolderId);
      
          setContextFolderId(()=>{
            self.destinationFolder = result.DestinationFolderId;
          }, StateContextKey.DestinationFolderId);
        }
      );
      
      this.repositoryService.getRepositoryFields(this.repoId).subscribe(
        result => {
          self.fields = result.data.map(field => ({ Id: field.props.Id, Name: field.props.Name}));
          this.initializeAllColumns();
        }
      )
    } else {
      this.initializeAllColumns();
    }

    this.initFileSetTable(data, forceUpdate);
    this.initializing = false;
    this.initialized = true;
    this.modelChange.emit(data);
  }

  initFileset(forceUpdate: boolean = false) {
    var self = this;

    new ResizeObserver(function() {
      self.zone.run(()=>{
        self.width = Math.max(MIN_FIELD_WIDTH, self.ele.nativeElement.offsetWidth);
      });
    }).observe(self.ele.nativeElement);
    
    this.errorChangedSub = this.validatorService.errorChanged.subscribe((errors: FileSetError[]) => {
      this.errorChanged.emit(errors);
    });

    window.addEventListener("message", this.boundReceiveMessage, false);

    if (this.initError == InitErrors.NONE) {
      
      this.initializing = true;

      if (isGuid(this.docId) && this.inState && this.mergeNew && !forceUpdate) {
        try {
          let stateObj = JSON.parse(this.inState);
          if (stateObj.IsDeleted) {
            this.readOnly = true;
            this.initError = InitErrors.RESOURCE_DELETED;
            this.initializing = false;
          } else {
            this.resource.getTemplate(this.docId)
              .subscribe(
                data => {
                  // initialize fileEntries
                  this.metaData = stateObj.Context.MetaDataCache.reduce((obj, item) => (obj[item.EntryId] = this.fieldParsingService.mapEntryModel(item), obj), {});
                  this.initFileSetEntryModel(stateObj.State.FileStates)

                  this.updateFilePropertyService.updateFileProperties(this.fileEntries, stateObj, data);

                  // copy over relevant data to new instance
                  data.Context.MetaDataCache = stateObj.Context.MetaDataCache;
                  data.State.FileStates = stateObj.State.FileStates;
      
                  if (this.inState) {
                    this.inState = JSON.stringify(data); // update inState
                  }
                  self.initialize(data, forceUpdate);
                },
                error => {
                  this.handleResourceError(error);
                });
          }
        } 
        catch(e)
        {
          console.error('unable to parse input state', e);
        }
      } else if (this.inState){
        try {
          let stateObj = JSON.parse(this.inState);
          if (stateObj.IsDeleted) {
            this.readOnly = true;
            this.initError = InitErrors.RESOURCE_DELETED;
            this.initializing = false;
          } else {
            if (forceUpdate){
              stateObj.Context.MetaDataCache = this.instanceModel.Context.MetaDataCache;
            } else {
              this.initInstanceSubject.next(stateObj);
            }
            self.initialize(stateObj, forceUpdate);
          }
        } 
        catch(e)
        {
          console.error('unable to parse input state', e);
        }
      } else if (isGuid(this.insId) && !this.createTemplate) {
        this.newInstanceCreated = true;
        this.resource.getInstance(this.insId)
          .subscribe(
            data => {
              if (forceUpdate){
                data.Context.MetaDataCache = this.instanceModel.Context.MetaDataCache;
              }
              self.initialize(data, true);
            },
            error => {
              this.handleResourceError(error);
            });
      } else if (isGuid(this.docId)) {
        this.initByFilesetId(this.docId, forceUpdate);
  
      } else {

        this.initError = InitErrors.NONE_INITIALIZED;
        this.errorInit();
      }
    
    } else {
      this.errorInit();
    }
  }

  initByFilesetId(id, forceUpdate) {

    this.docId = id;
    this.initializing = true;
    this.resource.getTemplate(id)
    .subscribe(
      data => {
        this.initializing = false;
        this.createTemplate = true;
        if (!this.newPropertyValueUpdated[StateContextKey.FileSetId]) {
          if (forceUpdate){
            data.Context.MetaDataCache = this.instanceModel.Context.MetaDataCache;
          } else {
            this.initInstanceSubject.next(data);
          }
          this.initialize(data, forceUpdate);
        } else if (forceUpdate) {
          data.Context.MetaDataCache = this.instanceModel.Context.MetaDataCache;
          this.initialize(data, forceUpdate);
        }
      },
      error => {
        this.handleResourceError(error);
      });
  }

  errorInit() {
    if (!this.displayColumns.length) {
      this.initColumn(DEFAULT_COLUMN_TO_USE);
    }

    this.initializing = false;
  }

  shouldDisableTable(): boolean {
    return this.readOnly || this.disabled;
  }

  onTypeChange(targetType) {
    let types = []; // types to be validated
    for (let item of this.fileEntries) {
      if (item.viewModel?.isSelected) {
        if (!types.find(t => t === item.docType)) {
          types.push(item.docType);
        }

        item.docType = targetType;
      }
    }

    this.tableRef.refreshTable();

    if (!types.find(t => t === targetType)) {
      types.push(targetType);
    }

    types.forEach(t => {
      this.validatorService.validateFileType(t, this.fileEntries);
    });
  }

  onUploadFile(fileInfo, targetType, origin: ImportOrigin) {
    if (this.fileEntries.length < this.uploadLimit) {
      let newFile: FileSetEntryViewModel = {
        id: fileInfo?.entryId?.toString(), 
        name: fileInfo.name, 
        docType: targetType, 
        status: (this.statusViewModel.length == 1) ? this.statusViewModel[0].key : EmptyPropertyKey.Status,  //use default status if only one is configured
        properties: fileInfo, 
        importOrigin: origin
      };

      this.instanceModel.Context.MetaDataCache.push(fileInfo.allProps);
        
      this.fileEntries.push(newFile);
      
      if (origin == ImportOrigin.LFREPO) {
        this.totalFilesAddedFromRepo += 1;
      } else if (origin == ImportOrigin.USERLOCAL) {
        this.totalFilesAddedFromLocal += 1;
      }

      this.documentCount = this.fileEntries.length;

      this.validatorService.validateFile(newFile);
      this.validatorService.validateFileType(targetType, this.fileEntries);
      this.modelChange.emit(this.state);
    } else {
      this.toastMessages = [ generateToast(LfMessageToastTypes.Error, this.localizePipe.transform({ key: 'lf_fileset_doc_limit_error', args: [this.uploadLimit] }, lfFilesetLocalize)) ];
    }
  }
  
  openImportRepoDialog(targetTypeId) {
    let fieldId = this.getAdditionalFields();
    this.dialogService.openDialog('FileBrowserDialog', {name: this.repoName, id: this.repoId, sourceFolder: this.defaultSourceFolder, fieldId: fieldId})
    .afterClosed().subscribe(
      (ret) => {
        if (ret && ret.length > 0) {
          this.initializing = true;
          let entries: number[] = ret.map((node) => Number(node.id));
          this.repositoryService.getBulkEntries(this.repoId, entries, fieldId).subscribe(
            (res) => {
              if (res && res.data) {
                let entryProperties = res.data.map((entry) => entry.props);
                let selectedEntries: EntryMetaData[] = this.fieldParsingService.parseEntries(entryProperties);
                selectedEntries.forEach(file => {
                  this.onUploadFile(file, targetTypeId, ImportOrigin.LFREPO);
                });
                this.initializing = false;
              }
            },
            (error) => {
              this.toastMessages = [ generateToast(LfMessageToastTypes.Error, this.localizePipe.transform({key: "lf_fileset_fail_to_select_repo_entry", args:[error]}, lfFilesetLocalize)) ];
              this.initializing = false;
            }
          );
        }
      });
  }

  openImportLocalDialog(targetTypeId) {
    this.targetType = targetTypeId;
    let url = `${this.waBaseURL}/UploadDialogPage-repo=${this.repoName}-folderId=${this.destinationFolder}-referrerId=${this.insId}-fieldIds=none`;
    this.importDialogRef = this.dialogService.openDialog('ImportLocalDialog', {
      importUrl: url
    });
  }

  receiveMessage: any = (event: any) =>  {
    // check the file set instance id matches
    let fileInfo = null;
    if (event.data && event.data.referrerId && event.data.referrerId === this.insId) {
      if (event.data.uploadCanceled) {
        this.importDialogRef.close(); // automatically close dialog when upload is canceled
      } else {
        this.repositoryService.getBulkEntries(this.repoId, [event.data.id], this.getAdditionalFields()).pipe(finalize(() => {
          if (this.tableRef) {
            this.tableRef.refreshTable();
          }
        })).subscribe(
          res => {
            if (res.data && res.data.length > 0){
              let parsedLocalData = this.fieldParsingService.parseEntries([res.data[0].props]);
              if (parsedLocalData && parsedLocalData.length > 0) {
                if (!parsedLocalData[0].entryId) {
                  parsedLocalData[0].entryId = prepareFallbackId(res.data[0].id);
                }
                this.onUploadFile(parsedLocalData[0], this.targetType, ImportOrigin.USERLOCAL);
              }

              if (event.data.uploadComplete) {
                this.importDialogRef.close(); // automatically close dialog when upload is complete
              }
            }
          },
          err => {
            // if failed to query meta data from local upload we should still add file to fileset with no data, close import dialog
            this.toastMessages = [ generateToast(LfMessageToastTypes.Warning, this.localizePipe.transform({ key: 'lf_fileset_fail_local_upload_metadata', args: [event.data.id] }, lfFilesetLocalize)) ];
            this.importDialogRef.close()

            let newFile: FileSetEntryViewModel = {
              id: event.data.id.toString(), 
              name: event.data.id.toString(), 
              docType: this.targetType, 
              status: (this.statusViewModel.length == 1) ? this.statusViewModel[0].key : EmptyPropertyKey.Status,  //use default status if only one is configured
              properties: null, 
              importOrigin: ImportOrigin.USERLOCAL
            } 

            this.fileEntries.push(newFile);
            this.totalFilesAddedFromLocal += 1;
            this.documentCount = this.fileEntries.length;
          }
        );
      }
    }
  }

  boundReceiveMessage = this.receiveMessage.bind(this);

  openImportPickerDialog(targetTypeId) {
    if (this.importMode == ImportMode.Local) {
      this.openImportLocalDialog(targetTypeId);
    }
    else if (this.importMode == ImportMode.Repository) {
      this.openImportRepoDialog(targetTypeId);
    }
    else if (this.importMode == ImportMode.Both) {
      this.dialogService.openDialog('ImportPickerDialog').afterClosed()
      .subscribe((result: number) => {
        if (result) {
          if (result === ImportMode.Local) {
            this.openImportLocalDialog(targetTypeId);
          } else {
            this.openImportRepoDialog(targetTypeId);
          }
        }
      });
    }
  }

  onDeleteSelectedDoc(){
    let index = this.fileEntries.length - 1;
    let types = []; // types to be validated
    while (index >= 0) {
      if (this.fileEntries[index].viewModel?.isSelected) {
        if (!types.find(t => t === this.fileEntries[index].docType)) {
          types.push(this.fileEntries[index].docType);
        }

        this.validatorService.removeError(ErrorType.RequiredStatus, TargetType.File, this.fileEntries[index].id);
        this.fileEntries.splice(index, 1);
      }
      index -= 1;
    }

    this.documentCount = this.fileEntries.length;
    this.tableRef.refreshTable();

    types.forEach(t => {
      this.validatorService.validateFileType(t, this.fileEntries);
    });
    this.modelChange.emit(this.state);
  }

  downLoadFile(response: any) {
    var fileName = response.headers.get('FileName');
    const blobFile = new Blob([response.body], {type: response.headers.get('Content-Type')});
    let downloadLink = document.getElementById('downloadFile');
    downloadLink.setAttribute('download', fileName);
    downloadLink.setAttribute('href', window.URL.createObjectURL(blobFile));
    downloadLink.click();
  }

  onDownloadSelectedDoc(){
    for (let file of this.fileEntries){
      if (file.viewModel?.isSelected){
        this.repositoryService.getEntryFile(this.repoId, file.id).subscribe(
          response => {
            if (response.status == 204){
              this.toastMessages = [ generateToast(LfMessageToastTypes.Warning ,this.localizePipe.transform("lf_fileset_no_content_to_export", lfFilesetLocalize)) ];
            }
            if (response.status == 200){
              this.downLoadFile(response)
            }
          },
          (error) => {
            if (error.status == 403) {
              this.toastMessages = [ generateToast(LfMessageToastTypes.Error, this.localizePipe.transform("lf_fileset_forbidden_access_error_message", lfFilesetLocalize)) ];
            } else {
              this.toastMessages = [ generateToast(LfMessageToastTypes.Error, this.localizePipe.transform("lf_fileset_failed_to_download_selected_file", lfFilesetLocalize)) ];
            }
          }
        );
        break;
      }
    }
  }

  onRefreshFileset(){
    this.initFileset(true);
  }

  onTableModelChanged() {
    this.modelChange.emit(this.state);
  }

  onValidate(dataInfo) { // validates updated table data
    if (dataInfo.type === TargetType.FileType) {
      this.validatorService.validateFileType(dataInfo.data, this.fileEntries);
    } else {
      if (dataInfo.removeError) {
        this.validatorService.removeError(ErrorType.RequiredStatus, TargetType.File, dataInfo.data);
      } else if (dataInfo.validateDuplicates) {
        this.validatorService.validateDuplicateFiles(dataInfo.data);
      } else {
        this.validatorService.validateFile(dataInfo.data);
      }
    }
    this.documentCount = this.fileEntries.length;
  }

  private handleResourceError(error) {
    if (error.status === 404) {
      this.disabled = true;
      this.initError = InitErrors.RESOURCE_NOT_FOUND;
    }
    this.initializing = false;
  }

  saveState() {
    if (this.insId) {
      let state = this.generateStateModel(this.fileEntries);
      let additionalColumns = this.displayColumns.map(column => column.key);
      if (this.instanceModel) {
        this.instanceModel.State.FileStates = state;
        this.instanceModel.Context.AdditionalColumns = additionalColumns;
        this.instanceModel.Context.ContextValueUpdated = this.newPropertyValueUpdated;
        
        // remove cache that does not belong in the filestates
        if (this.instanceModel.Context.MetaDataCache) {
          this.instanceModel.Context.MetaDataCache =  this.instanceModel.Context.MetaDataCache.filter(cache =>{
            return this.instanceModel.State.FileStates.find(f => f.FileId == cache.EntryId.toString());
          });
        }

        this.instanceModel.Context.StatNewFilesAddedFromLocal = this.totalFilesAddedFromLocal;
        this.instanceModel.Context.StatNewFilesAddedFromRepo = this.totalFilesAddedFromRepo;
         
      }
    }

  }

  private generateStateModel(items: FileSetEntryViewModel[]): FileStateModel[] {
    return items.map(item=>({
      FileId: item.id,
      FileStateValues: [
        {StateKey: 'status', Value: item.status}, 
        {StateKey: 'type', Value: item.docType}, 
        {StateKey: 'importorigin', Value: item.importOrigin}
      ]
    }));
  
  }

  private getAdditionalFields(){
    let additionalFields = [];
    if (this.additionalColumns) {
      additionalFields = Array.from(new Set(this.additionalColumns.filter(column => !isNaN(+column))));
    }
    return additionalFields;
  }

  saveInstance() {
    this.saveState();

    if (!this.newInstanceCreated) {
      this.resource.newInstance(this.docId, this.instanceModel).subscribe(result => {
        this.newInstanceCreated = true;
      });
    } else {
      this.resource.saveInstance(this.insId, this.instanceModel.State).subscribe();
    }
  }

  ngOnDestroy() {
    // cleanup
    if (this.errorChangedSub) {
      this.errorChangedSub.unsubscribe();
    }

    window.removeEventListener("message", this.boundReceiveMessage, false);
  }
}


